import { useState, useCallback, useRef, DragEvent, RefObject } from "react";
import { useSegment } from "../../../hooks/use-segment";
import { useUploadsStore } from "./use-uploads-store";

/* Prop Types */
interface UseDragAndDropProps {
  accept?: string[];
  inputRef: RefObject<HTMLInputElement | null>;
  segmentTrackName: string;
  segmentTrackMediaType: string;
  additionalSegmentProperties?: Record<string, unknown>;
}

/* Constants */
const MAX_DIMENSION = 4096;

/*
 * useDragAndDrop custom hook
 * This hook is used to handle all drag and drop logic
 */
export const useDragAndDrop = ({ accept, inputRef, segmentTrackName, segmentTrackMediaType, additionalSegmentProperties }: UseDragAndDropProps) => {
  /* State */
  const [isDragging, setIsDragging] = useState(false);

  /* Hooks */
  const { segmentTrackEvent } = useSegment();

  /* Stores */
  const uploads = useUploadsStore((state) => state.uploads);
  const { add, updateName } = useUploadsStore((state) => state.uploadActions);

  /* Refs */
  const dragCounter = useRef(0);

  /* Handlers */
  const handleAdd = useCallback(
    async (files: File[]) => {
      /* Get the width and height of a staged image */
      const getImageDimensions = (file: File) => {
        return new Promise<{ width: number; height: number }>((resolve) => {
          const img = new Image();
          img.onload = () => {
            resolve({ width: img.width, height: img.height });
          };
          img.src = URL.createObjectURL(file);
        });
      };

      /* Make sure the staged image isn't bigger than the max dimensions */
      const validateImageDimensions = (width: number, height: number): { isValid: boolean; message?: string } => {
        if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
          return {
            isValid: false,
            message: `Image dimensions exceed maximum allowed size of ${MAX_DIMENSION}x${MAX_DIMENSION}px`,
          };
        }
        return { isValid: true };
      };

      /* Loop through each file and add to store */
      for (const file of files) {
        const dims = await getImageDimensions(file);

        // Segment event for each staged image
        segmentTrackEvent(`${segmentTrackName} ${segmentTrackMediaType} Staged`, {
          ...additionalSegmentProperties,
          fileName: file.name,
          fileSize: file.size,
          width: dims.width,
          height: dims.height,
        });

        const validation = validateImageDimensions(dims.width, dims.height);

        // create a temporary key for the image as the key is not available until the image is uploaded
        const tempKey = crypto.randomUUID();
        add({ key: tempKey, name: file.name, file, status: "queued", width: dims.width, height: dims.height, isValid: validation.isValid });

        // Reset input value to clear the file input
        if (inputRef?.current) inputRef.current.value = "";
      }

      // Reset input value to clear the file input
      if (inputRef?.current) inputRef.current.value = "";
    },
    [add, inputRef, segmentTrackEvent, segmentTrackName, segmentTrackMediaType, additionalSegmentProperties],
  );

  /* Check the files and add to store when dropped into the UI */
  const handleDrop = useCallback(
    async (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      dragCounter.current = 0;

      const files = Array.from(e.dataTransfer.files);

      // Filter out files that don't match the accept prop
      const validFiles = accept
        ? files.filter((file) => {
            const fileExtension = `.${file.name.split(".").pop()?.toLowerCase()}`;
            return accept.some((type) => type.toLowerCase() === fileExtension);
          })
        : files;

      // Add valid files to the store
      if (validFiles.length) {
        handleAdd(validFiles);
      }
    },
    [accept, handleAdd],
  );

  /* The user can edit the file name they've staged */
  const handleNameSave = (oldName: string, newName: string) => {
    const upload = uploads.find((upload) => upload.name === oldName);
    if (upload) {
      // Segment event for each renamed image
      segmentTrackEvent(`${segmentTrackName} Staged ${segmentTrackMediaType} Renamed`, {
        ...additionalSegmentProperties,
        from: oldName,
        to: newName,
      });

      // remove the old asset and add the new one
      updateName(upload.key, newName);
    }
  };

  const handleDragEnter = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current += 1;
    setIsDragging(true);
  }, []);

  const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragLeave = useCallback((e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current -= 1;
    if (dragCounter.current === 0) {
      setIsDragging(false);
    }
  }, []);

  /* Return */
  return {
    isDragging,
    dragBindings: {
      onDragEnter: handleDragEnter,
      onDragOver: handleDragOver,
      onDragLeave: handleDragLeave,
      onDrop: handleDrop,
    },
    handleAdd,
    handleNameSave,
  };
};
